import { LogoutOutlined, UserOutlined } from "@ant-design/icons";
import { Button, Dropdown, Tooltip } from "antd";
import type { AvatarProps, MenuProps } from "antd";
import type { ReactNode } from "react";
import { t } from "@airange/shared/i18n-admin-controls";
import { IdentityUserText } from "./identity-user-text";

export type AdminShellLinkItem = {
  key: string;
  label: ReactNode;
  href: string;
  icon?: ReactNode;
};

export type AdminShellUser = {
  username: string;
  full_name?: string | null;
  avatar_url?: string | null;
};

export type AdminShellActionsProps = {
  localeNode?: ReactNode;
  links?: AdminShellLinkItem[];
};

function firstChar(value: string) {
  const normalized = String(value || "").trim();
  return normalized.slice(0, 1) || "A";
}

export type AdminShellAvatarConfig = {
  currentUser?: AdminShellUser | null;
  onLogout?: () => void | Promise<void>;
};

export function AdminShellActions({ localeNode, links = [] }: AdminShellActionsProps) {
  const visibleLinks = links.filter((item) => item.href);

  return [
    localeNode ? <span key="locale">{localeNode}</span> : null,
    ...visibleLinks.map((item) => (
      item.icon ? (
        <Tooltip key={item.key} title={item.label}>
          <a href={item.href} target="_blank" rel="noreferrer">
            <Button variant="text" icon={item.icon} aria-label={typeof item.label === "string" ? item.label : undefined} />
          </a>
        </Tooltip>
      ) : (
        <a key={item.key} href={item.href} target="_blank" rel="noreferrer">
          {item.label}
        </a>
      )
    )),
  ].filter(Boolean);
}

export function buildAdminAvatarProps({ currentUser, onLogout }: AdminShellAvatarConfig) {
  if (!currentUser) {
    return false;
  }

  const titleText = currentUser.full_name || currentUser.username;
  const menuItems: MenuProps["items"] = onLogout
    ? [
        {
          key: "logout",
          icon: <LogoutOutlined />,
          label: t("common.actions.logout"),
          onClick: () => {
            void onLogout();
          },
        },
      ]
    : [];

  const avatarProps: AvatarProps & {
    title?: ReactNode;
    render?: (props: AvatarProps, defaultDom: ReactNode) => ReactNode;
  } = {
    src: currentUser.avatar_url || undefined,
    icon: <UserOutlined />,
    children: firstChar(titleText),
    style: { textTransform: "uppercase" },
    title: (
      <IdentityUserText
        username={currentUser.username}
        {...(currentUser.full_name === undefined ? {} : { fullName: currentUser.full_name })}
        displayText={titleText}
        detailLabels={{
          username: t("common.fields.username"),
          fullName: t("common.fields.displayName"),
        }}
      />
    ),
  };

  if (menuItems.length) {
    avatarProps.render = (_props, defaultDom) => (
      <Dropdown menu={{ items: menuItems }} placement="bottomRight" trigger={["click"]}>
        <span>{defaultDom}</span>
      </Dropdown>
    );
  }

  return avatarProps;
}
